/**
 * New Releases Block
 * Shows recently published series, comics, and novels
 * Supports curated mode with fallback to algorithmic
 */
import React from 'react';
import { Clock, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SectionHeader, SeriesCardVertical, resolveImageUrl } from './shared';

const NewReleasesBlock = ({ data = [], settings = {} }) => {
  // Handle both old format (array) and new format ({items, settings})
  let items = [];
  let dataSettings = {};
  
  if (Array.isArray(data)) {
    items = data;
  } else if (data && typeof data === 'object') {
    items = data.items || [];
    dataSettings = data.settings || {};
  }
  
  const {
    title = 'Newly Released Originals',
    max_items = 12,
    card_scale = 0.9,
    max_image_height = 240,
    columns = { desktop: 6, tablet: 4, mobile: 3 },
  } = settings;

  if (items.length === 0) return null;

  const displayItems = items.slice(0, max_items);
  
  // Build link based on link_type
  const getItemLink = (item) => {
    const linkType = item.link_type;
    const linkTarget = item.link_target || item.id;
    
    switch (linkType) {
      case 'novel':
        return `/novels/${linkTarget}`;
      case 'series':
      case 'comic':
      default:
        return `/series/${linkTarget}`;
    }
  };

  return (
    <section data-testid="new-releases-block">
      <SectionHeader 
        title={title} 
        icon={Clock}
        iconColor="text-green-400"
        viewAllLink="/browse?filter=new"
        badge={dataSettings.mode === 'curated' ? (
          <span className="ml-2 px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded-full flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Curated
          </span>
        ) : null}
      />
      
      <div 
        className={`grid gap-2 sm:gap-4 px-2 sm:px-6`}
        style={{
          gridTemplateColumns: `repeat(${columns.mobile}, 1fr)`,
        }}
      >
        <style>{`
          @media (min-width: 640px) {
            [data-testid="new-releases-block"] > div:last-child {
              grid-template-columns: repeat(${columns.tablet}, 1fr) !important;
            }
          }
          @media (min-width: 1024px) {
            [data-testid="new-releases-block"] > div:last-child {
              grid-template-columns: repeat(${columns.desktop}, 1fr) !important;
            }
          }
        `}</style>
        {displayItems.map((item) => (
          <Link 
            key={`${item.link_type}-${item.id}`}
            to={getItemLink(item)}
            className="group block"
          >
            <div 
              className="relative overflow-hidden rounded-lg bg-gray-900"
              style={{ 
                transform: `scale(${card_scale})`,
                transformOrigin: 'top center'
              }}
            >
              {/* Cover Image */}
              <div 
                className="relative w-full overflow-hidden"
                style={{ maxHeight: `${max_image_height}px` }}
              >
                <img
                  src={resolveImageUrl(item.cover_url) || 'https://images.unsplash.com/photo-1612036782180-6f0b6cd846fe?w=300&h=400&fit=crop'}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                
                {/* Content Type Badge */}
                {item.link_type === 'novel' && (
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-purple-500/80 text-white text-xs rounded">
                    Novel
                  </div>
                )}
                
                {/* NEW Badge */}
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-green-500/80 text-white text-xs rounded font-medium">
                  NEW
                </div>
                
                {/* Gradient Overlay */}
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
              </div>
              
              {/* Info */}
              <div className="p-2 sm:p-3">
                <h3 className="text-sm font-medium text-white truncate group-hover:text-orange-400 transition-colors">
                  {item.title}
                </h3>
                <p className="text-xs text-gray-400 truncate mt-0.5">
                  {item.creator_name || 'Unknown Creator'}
                </p>
                {item.genres && item.genres.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {item.genres.slice(0, 2).map((genre, idx) => (
                      <span 
                        key={idx}
                        className="px-1.5 py-0.5 text-[10px] bg-gray-800 text-gray-400 rounded"
                      >
                        {genre}
                      </span>
                    ))}
                  </div>
                )}
                {item.created_at && (
                  <p className="text-[10px] text-gray-500 mt-1">
                    {new Date(item.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default NewReleasesBlock;
